import type { InfiniteData, QueryFunctionContext } from '@tanstack/react-query';
import { infiniteQueryOptions, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import type { PaginationParams, SortingParams } from 'lib/api';
import { apiClient } from 'lib/api';
import type { AdsLibraryListResponse, GoogleCreative, GoogleCreativeDTO, GoogleCreativeRegionStat, GoogleCreativeRegionStatDTO, GoogleCreativeVariation } from './types';
import type { GoogleAdType } from '../types';
import { parseAdsLibraryApiPath } from './parseAdsLibraryApiPath';

interface FilteringFields {
  network: string;
  region: string;
  company_id: number[];
  domain: string[];
  advertiser_id: string[];
  format: GoogleAdType[];
  date_from: string;
  date_to: string;
  new_only: boolean;
  last_shown_recently: boolean;
  include_welltech: boolean;
}

type GetGoogleCreativesOptions = PaginationParams & Partial<SortingParams> & Partial<FilteringFields>;

type RequestParams = Omit<GetGoogleCreativesOptions, 'network' | 'region' | 'offset'>;

const getGoogleCreativeVariationFromDTO = (variation: GoogleCreativeDTO['variations'][number]): GoogleCreativeVariation => ({
  imageUrl: variation.image_url,
  s3ImageUrl: variation.s3_image_url,
  videoUrl: variation.video_url,
  s3VideoUrl: variation.s3_video_url,
  thumbnailUrl: variation.thumbnail_url,
  s3ThumbnailUrl: variation.s3_thumbnail_url,
  clickUrl: variation.click_url,
  cta: variation.cta,
  headline: variation.headline,
  description: variation.description,
});

const getGoogleCreativeRegionStatFromDTO = (stat: GoogleCreativeRegionStatDTO): GoogleCreativeRegionStat => ({
  regionCode: stat.region_code,
  regionName: stat.region_name,
  lastShownOn: stat.last_shown_on,
});

const getGoogleCreativeFromDTO = (creative: GoogleCreativeDTO): GoogleCreative => ({
  adCreativeId: creative.ad_creative_id,
  adLibraryUrl: creative.ad_library_url,
  firstShownOn: creative.first_shown_on,
  lastShownOn: creative.last_shown_on,
  format: creative.format,
  advertiserId: creative.advertiser_id,
  advertiserName: creative.advertiser_name,
  company: creative.company,
  domains: creative.domains,
  winnerConfidenceScore: creative.winner_confidence_score,
  longevity: creative.longevity,
  variationsCount: creative.variations_count,
  variations: creative.variations.map(getGoogleCreativeVariationFromDTO),
  regionStats: (creative.region_stats ?? []).map(getGoogleCreativeRegionStatFromDTO),
});

const composeRequestParams = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  network,
  region,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  offset,
  new_only,
  last_shown_recently,
  include_welltech,
  ...restParams
}: GetGoogleCreativesOptions): RequestParams => {
  return {
    ...restParams,
    ...(region === 'us' && new_only !== undefined && { new_only }),
    ...(last_shown_recently && { last_shown_recently }),
    ...(include_welltech !== undefined && { include_welltech }),
  };
};

export const getGoogleCreatives = async ({
  signal,
  queryKey,
  pageParam,
}: QueryFunctionContext<[string, string, RequestParams], number>): Promise<AdsLibraryListResponse<GoogleCreative>> => {
  const res = await apiClient.get<AdsLibraryListResponse<GoogleCreativeDTO>>(queryKey[1], {
    params: { ...queryKey[2], offset: pageParam },
    signal,
  });
  return {
    ...res.data,
    data: res.data.data.map(getGoogleCreativeFromDTO),
  };
};

export interface GoogleCreativesData {
  pages: GoogleCreative[][];
  count: number;
  loadTs?: string;
}

export const getGoogleCreativesOptions = (params: GetGoogleCreativesOptions) =>
  infiniteQueryOptions<
    AdsLibraryListResponse<GoogleCreative>,
    Error,
    GoogleCreativesData,
    [string, string, RequestParams],
    number
  >({
    staleTime: 60 * 60 * 1000,
    queryFn: getGoogleCreatives,
    queryKey: [
      'google-creatives',
      parseAdsLibraryApiPath('ad_creatives', { network: params.network, region: params.region }),
      composeRequestParams(params),
    ],
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.data.length) {
        return null;
      }
      const nextOffset = lastPageParam + lastPage.meta.limit;
      return nextOffset < lastPage.meta.count ? nextOffset : null;
    },
    select: (data: InfiniteData<AdsLibraryListResponse<GoogleCreative>>) => ({
      count: data.pages[0]?.meta.count ?? 0,
      loadTs: data.pages[0]?.meta.load_ts,
      pages: data.pages.map((page) => page.data),
    }),
  });

export const useGoogleCreatives = (params: GetGoogleCreativesOptions) =>
  useSuspenseInfiniteQuery(getGoogleCreativesOptions(params));
